import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Loader2, PartyPopper, Crown, ArrowRight } from 'lucide-react';
import confetti from 'canvas-confetti';

const API = process.env.REACT_APP_BACKEND_URL;

function SubscriptionSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [paymentDetails, setPaymentDetails] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      verifyPayment();
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const verifyPayment = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API}/api/subscription/checkout/status/${sessionId}`, {
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });

      if (!res.ok) {
        throw new Error('Failed to verify payment');
      }

      const data = await res.json();
      setPaymentDetails(data);

      if (data.payment_status === 'paid') {
        setStatus('success');
        // Trigger confetti
        setTimeout(() => {
          confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 },
            colors: ['#00D084', '#6366F1', '#F59E0B']
          });
        }, 300);
      } else {
        setStatus('error');
      }
    } catch (error) {
      console.error('Payment verification error:', error);
      setStatus('error');
    }
  };

  if (status === 'verifying') {
    return (
      <div className="min-h-screen bg-guap-dark flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-guap-green animate-spin mx-auto mb-4" />
          <h2 className="font-anton text-2xl text-white mb-2">Verifying Payment...</h2>
          <p className="text-guap-muted">Please wait while we confirm your subscription</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-guap-dark flex items-center justify-center p-4">
        <div className="bg-guap-card border border-guap-border rounded-2xl p-8 max-w-md text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-500/20 rounded-full flex items-center justify-center">
            <span className="text-3xl">⚠️</span>
          </div>
          <h2 className="font-anton text-2xl text-white mb-2">Payment Verification Issue</h2>
          <p className="text-guap-muted mb-6">
            We couldn't verify your payment. If you were charged, your subscription will be activated automatically within a few minutes.
          </p>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/app/dashboard')}
              className="w-full py-3 bg-guap-green text-black font-bold rounded-xl hover:bg-guap-green/90 transition-colors"
            >
              Go to Dashboard
            </button>
            <button
              onClick={() => navigate('/app/pricing')}
              className="w-full py-3 bg-guap-card border border-guap-border text-white font-bold rounded-xl hover:bg-guap-border/50 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-guap-dark flex items-center justify-center p-4">
      <div className="bg-guap-card border border-guap-green/30 rounded-2xl p-8 max-w-md text-center">
        {/* Success Icon */}
        <div className="relative mb-6">
          <div className="w-20 h-20 mx-auto bg-guap-green/20 rounded-full flex items-center justify-center">
            <Crown className="w-10 h-10 text-guap-green" />
          </div>
          <div className="absolute -top-2 -right-2 w-8 h-8 bg-guap-yellow rounded-full flex items-center justify-center animate-bounce">
            <PartyPopper className="w-4 h-4 text-black" />
          </div>
        </div>

        {/* Title */}
        <h1 className="font-anton text-3xl text-white mb-2">
          Welcome to <span className="text-guap-green">Premium!</span>
        </h1>
        <p className="text-guap-muted mb-6">
          Your subscription is now active. Time to unlock your full financial potential!
        </p>

        {/* Payment Summary */}
        {paymentDetails && (
          <div className="bg-guap-dark rounded-xl p-4 mb-6 text-left">
            <div className="flex justify-between items-center mb-2">
              <span className="text-guap-muted">Amount Paid</span>
              <span className="text-white font-bold">
                ${(paymentDetails.amount_total / 100).toFixed(2)} {paymentDetails.currency?.toUpperCase()}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-guap-muted">Status</span>
              <span className="flex items-center gap-1 text-guap-green">
                <Check className="w-4 h-4" />
                Active
              </span>
            </div>
          </div>
        )}

        {/* Features Unlocked */}
        <div className="text-left mb-6">
          <h3 className="text-white font-bold mb-3">🎉 Features Unlocked:</h3>
          <ul className="space-y-2">
            <li className="flex items-center gap-2 text-sm text-gray-300">
              <Check className="w-4 h-4 text-guap-green flex-shrink-0" />
              Automatic Bank Sync with Plaid
            </li>
            <li className="flex items-center gap-2 text-sm text-gray-300">
              <Check className="w-4 h-4 text-guap-green flex-shrink-0" />
              AI-Powered Financial Insights
            </li>
            <li className="flex items-center gap-2 text-sm text-gray-300">
              <Check className="w-4 h-4 text-guap-green flex-shrink-0" />
              Premium Support
            </li>
          </ul>
        </div>

        {/* CTA */}
        <button
          onClick={() => navigate('/app/dashboard')}
          className="w-full py-4 bg-guap-green text-black font-bold rounded-xl hover:bg-guap-green/90 transition-colors flex items-center justify-center gap-2"
          data-testid="go-to-dashboard-btn"
        >
          Start Exploring
          <ArrowRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

export default SubscriptionSuccess;
